import { paths } from '../../../config/paths.config';
import Scene from '../../scene/scene';
import Element from '../element';
import Ship, { ShipParams } from '../ship/ship';
import Resource, { ResourceParams } from '../resource/resource';

import { Faction } from '../../../utils/faction';
import { round } from '../../../utils/math';
import { getXByDistanceAndAngle, getYByDistanceAndAngle } from '../../../utils/geometry';
import { randomInArray } from '../../../utils/array';
import { Iterator } from '../../../utils/types';

enum rotationDirection {
  clockwise = 'clockwise',
  antiClockwise = 'antiClockwise',
}

export interface AsteroidParams extends ShipParams {
  rotationDirection?: rotationDirection
}

export default class Asteroid extends Ship implements AsteroidParams {
  rotationDirection: rotationDirection = rotationDirection.clockwise;

  faction = Faction.HAZARD;

  static generateAsteroids(params: {
    scene: Scene,
    context: CanvasRenderingContext2D,
    centerX?: number,
    centerY?: number,
    maxDistance?: number,
    number?: number,
    moveAngle?: number,
    moveSpeed?: number,
    canStack?: boolean
  }): void {
    const {
      scene,
      context,
      centerX = 0,
      centerY = 0,
      maxDistance = 1000,
      number = 1,
      moveAngle = 270,
      moveSpeed = 0,
      canStack = false,
    } = params;

    let retries = 0;
    const retriesLimit = 100;

    for (let i = 0; i < number; i++) {
      const distanceFromCenter = round(Math.random() * maxDistance);
      const angleFromCenter = round(Math.random() * 360);
      const x = getXByDistanceAndAngle({ x: centerX, distance: distanceFromCenter, angle: angleFromCenter });
      const y = getYByDistanceAndAngle({ y: centerY, distance: distanceFromCenter, angle: angleFromCenter });

      const width = round(Math.max(Math.random() * 200, 50));
      const height = width;

      if (
        !canStack &&
        scene.quadtree.colliding({
          x: x - width / 2,
          y: y - height / 2,
          width,
          height,
        }).length > 0
      ) {
        i--;
        retries++;
        if (retries >= retriesLimit)
          break;
        continue;
      }

      new Asteroid({
        scene,
        context,
        x,
        y,
        width,
        height,
        moveAngle,
        moveSpeed: round(moveSpeed + Math.random() * moveSpeed / 4),
        lookAngle: angleFromCenter,
        lookSpeed: 3600 / width * Math.random(), // the bigger, the slower
        HP: width, // the bigger, the tougher
        rotationDirection: randomInArray([rotationDirection.clockwise, rotationDirection.antiClockwise]),
        spriteParams: {
          src: `${paths.img.hazards}/${randomInArray(['asteroid.png', 'asteroid2.png'])}`
        }
      } as AsteroidParams)
        .create()
        ;
    }
  }

  update(modifier: number): Asteroid {
    if (!this.isInSceneBoundaries) {
      this.remove();
      return this;
    }

    super.update(modifier);

    const angleToReach = this.rotationDirection === 'antiClockwise'
      ? this.lookAngle + 10
      : this.lookAngle - 10
      ;
    this.rotateTowardsAngle(angleToReach, modifier);

    return this;
  }

  takeDamages(amount: number): void {
    return super.takeDamages(amount / 4); // asteroids are TANKY
  }

  remove(): Iterator<Element> {
    /* drop a random number of resources on destruction */
    const chunksNumber = Math.ceil(Math.random() * 4);
    for (let i = 0; i < chunksNumber; i++) {
      const chunk = new Resource({
        scene: this.scene,
        context: this.context,
        x: this.x + Math.random() * this.width / 4,
        y: this.y + Math.random() * this.height / 4,
        width: Math.max(20, this.width / 4),
        height: Math.max(20, this.height / 4),
        amount: Math.max(1, Math.round(this.width / 10)),
        spriteParams: {
          src: `${paths.img.hazards}/money2.png`,
        },

      } as ResourceParams);
      chunk.externalVector = {
        angle: Math.random() * 360,
        speed: 100,
        duration: 2000,
        deceleration: 50,
        minSpeed: 0,
      };
      chunk.create();
    }
    return super.remove();
  }
}
